<div class="home" [class.vertical]="layoutOrientation() === 'vertical'">
  <jotjson-drop-overlay [visible]="dropActive()" />
  <jj-app-header>
    <jj-toolbar
      [hasContent]="hasContent()"
      [title]="title()"
      [canSave]="canSave()"
      [isSavedBlob]="loadedBlob() !== null"
      [isDirty]="dirty()"
      [isOwnedBlob]="isOwnedBlob()"
      [saveInFlight]="saveInFlight()"
      [isOwner]="isOwnedBlob()"
      [isFileBacked]="isFileBacked()"
      [filename]="fileBackedFilename()"
      [clipboardState]="clipboardState()"
      [clipboardPreview]="clipboardPreview()"
      [paneLayout]="paneLayout()"
      [loadedBlobTitle]="loadedBlob()?.title ?? null"
      [suggestedTitles]="suggestedTitlesForMenu()"
      [wandEnabled]="wandEnabled()"
      [beaconIndex]="treeBeaconIndex()"
      (pasteRequested)="onPaste()"
      (copyRequested)="onCopy()"
      (copyEscaped)="onCopyEscaped()"
      (upload)="onUpload($event)"
      (localFilePicked)="onLocalFilePicked($event)"
      (saveAsNewFile)="onSaveAsNewFile()"
      (saveAsBlob)="onSaveAsBlob()"
      (download)="onDownload()"
      (clear)="onClear()"
      (format)="onFormat()"
      (minify)="onMinify()"
      (sort)="onSort()"
      (paneLayoutChange)="onPaneLayoutChange($event)"
      (toggleTheme)="onToggleTheme()"
      (toggleSelectionSync)="onToggleSelectionSync()"
      (save)="onSave()"
      (signInRequested)="onSignInRequested()"
      (titleChange)="onTitleChange($event)"
      (copyShareLink)="onCopyShareLink()"
      (deleteBlob)="onDeleteBlob()"
      (suggestRequested)="onSuggestRequested()"
    ></jj-toolbar>
  </jj-app-header>

  <main id="main-content" class="home-main" tabindex="-1" #homeFocusFallback>
    @if (isBrowser) {
      <h1 class="sr-only" i18n="@@home.h1">JSON editor</h1>
    }
    <div
      class="split"
      [class.editor-only]="effectivePaneVisibility() === 'editor-only'"
      [class.tree-only]="effectivePaneVisibility() === 'tree-only'"
      [style]="splitStyle()"
      #splitHost
    >
      @if (isBrowser) {
        <section
          class="pane pane-editor"
          aria-label="JSON input"
          i18n-aria-label="@@home.editorPane.aria"
        >
          <jj-clipboard-banner></jj-clipboard-banner>
          <jj-cold-boot-clipboard-banner
            [visible]="coldBootClipboardBannerVisible()"
            (choice)="onColdBootClipboardChoice($event)"
          ></jj-cold-boot-clipboard-banner>
          <jotjson-extract-json-banner
            [visible]="extractBannerVisible()"
            [blockCount]="extractedCandidate()?.data?.blockCount ?? 0"
            [commentsWillBeDropped]="
              (extractedCandidate()?.data?.hasComments ?? false) &&
              !(extractedCandidate()?.data?.preservesComments ?? true)
            "
            (extract)="onExtractAccept()"
            (dismiss)="onExtractDismiss()"
          ></jotjson-extract-json-banner>
          <jj-upload-error-banner
            [visible]="uploadErrorVisible()"
            [filename]="uploadErrorFilename()"
            (dismiss)="onUploadErrorDismiss()"
          ></jj-upload-error-banner>
          <jj-json-editor
            [value]="content()"
            [errors]="errors()"
            (valueChange)="onValueChange($event)"
            (cursorPositionChange)="onCursorChange($event)"
            (paste)="onEditorPaste($event)"
          ></jj-json-editor>
        </section>

        <div
          class="splitter jj-no-select"
          role="separator"
          aria-orientation="vertical"
          aria-valuenow="50"
          aria-valuemin="0"
          aria-valuemax="100"
          tabindex="-1"
          (pointerdown)="onSplitterPointerDown($event)"
        ></div>

        <section
          class="pane pane-tree"
          aria-label="Tree view"
          i18n-aria-label="@@home.treePane.aria"
          #treeHost
        >
          <jj-rule-sets-toolbar></jj-rule-sets-toolbar>
          <jj-json-tree
            [value]="treePaneInputs().value"
            [commentsByPath]="treePaneInputs().commentsByPath"
            [viewResetToken]="viewResetTokenValue()"
            [highlights]="highlights()"
            [canEditHighlights]="canEditHighlights()"
            [extractCandidates]="treeStringExtractor.candidates()"
            [extractSourceVersion]="treeStringExtractor.currentVersion()"
            (selectionChange)="onTreeSelectionChange($event)"
            (extractRequest)="onExtractRequest($event)"
            (sortKeysRequest)="onSortKeysRequest($event)"
            (applyDecodedRequest)="onApplyDecodedRequest($event)"
            (highlightsChange)="onHighlightsChange($event)"
          ></jj-json-tree>
        </section>
      } @else {
        <section class="server-skeleton" aria-hidden="true">
          <h1 i18n="@@home.server.brand">JotJSON</h1>
          <p i18n="@@home.server.tagline">JSON viewer, formatter, and tree explorer</p>
          <p i18n="@@home.server.description">
            Paste, format, validate, and share JSON or JSONC. Browse large documents in a fast tree
            explorer with formatting rules, history, and shareable links.
          </p>
        </section>
      }
    </div>

    @if (isBrowser) {
      <jj-status-bar
        [text]="content()"
        [mode]="mode()"
        [parseResult]="parseResult()"
        [cursor]="cursor()"
        [identity]="statusBarIdentity()"
      ></jj-status-bar>
    }
  </main>
</div>
